/**
 * Per-note Y.Doc manager. Each note is its own doc (content in Y.Text),
 * persisted to its own IndexedDB database and refcounted so tabs/panes can
 * share one instance. Sync providers attach here in the sync phase.
 *
 * INVARIANT: a note doc is NEVER rebuilt from markdown text. Import creates
 * fresh docs; export reads ytext.toString().
 */
import * as Y from "yjs";
import { IndexeddbPersistence } from "y-indexeddb";
import { DOC_KEYS, docName } from "@hydra-notes/shared";
import { crossTabSync } from "./crosstab";
import type { AttachOpts, SyncManager } from "./sync";

export interface NoteHandle {
  noteId: string;
  doc: Y.Doc;
  text: Y.Text;
  whenLoaded: Promise<void>;
  release: () => void;
}

interface Entry {
  doc: Y.Doc;
  persistence: IndexeddbPersistence | null;
  releaseCrossTab: () => void;
  releaseSync: (() => void) | null;
  whenLoaded: Promise<void>;
  refs: number;
  closeTimer: ReturnType<typeof setTimeout> | null;
}

const CLOSE_DELAY_MS = 30_000; // keep recently-closed docs warm

export interface NoteDocManagerOpts {
  /** false = no IndexedDB (shared folders are online-only). */
  persist?: boolean;
  /** Passed through to sync attach: token space + presence + auth failures. */
  attach?: Omit<AttachOpts, "spaceId">;
}

export class NoteDocManager {
  private entries = new Map<string, Entry>();
  private readonly persist: boolean;

  constructor(
    readonly workspaceId: string,
    private readonly sync: SyncManager | null = null,
    private readonly opts: NoteDocManagerOpts = {},
  ) {
    this.persist = opts.persist ?? true;
  }

  private dbName(noteId: string) {
    return `hn.note.${this.workspaceId}.${noteId}`;
  }

  open(noteId: string): NoteHandle {
    let entry = this.entries.get(noteId);
    if (!entry) {
      const doc = new Y.Doc({ gc: true });
      const persistence = this.persist ? new IndexeddbPersistence(this.dbName(noteId), doc) : null;
      const attachment = this.sync
        ? this.sync.attach(docName.note(this.workspaceId, noteId), doc, {
            spaceId: this.persist ? undefined : this.workspaceId,
            ...this.opts.attach,
          })
        : null;
      entry = {
        doc,
        persistence,
        releaseCrossTab: crossTabSync(this.dbName(noteId), doc),
        releaseSync: attachment ? attachment.release : null,
        // online-only docs are "loaded" at first server sync instead
        whenLoaded: persistence ? persistence.whenSynced.then(() => undefined) : (attachment?.whenSynced ?? Promise.resolve()),
        refs: 0,
        closeTimer: null,
      };
      this.entries.set(noteId, entry);
    }
    if (entry.closeTimer) {
      clearTimeout(entry.closeTimer);
      entry.closeTimer = null;
    }
    entry.refs++;

    let released = false;
    return {
      noteId,
      doc: entry.doc,
      text: entry.doc.getText(DOC_KEYS.noteContent),
      whenLoaded: entry.whenLoaded,
      release: () => {
        if (released) return;
        released = true;
        entry.refs--;
        if (entry.refs <= 0) {
          entry.closeTimer = setTimeout(() => this.close(noteId), CLOSE_DELAY_MS);
        }
      },
    };
  }

  /** Read a note's markdown without keeping it open. */
  async readMarkdown(noteId: string): Promise<string> {
    const h = this.open(noteId);
    await h.whenLoaded;
    const text = h.text.toString();
    h.release();
    return text;
  }

  private close(noteId: string) {
    const entry = this.entries.get(noteId);
    if (!entry || entry.refs > 0) return;
    entry.releaseSync?.();
    entry.releaseCrossTab();
    entry.persistence?.destroy();
    entry.doc.destroy();
    this.entries.delete(noteId);
  }

  /** Permanently destroy a note's local storage (after permanent delete). */
  async destroyStorage(noteId: string) {
    const entry = this.entries.get(noteId);
    if (entry) {
      entry.releaseSync?.();
      entry.releaseCrossTab();
      entry.doc.destroy();
      if (entry.persistence) await entry.persistence.clearData();
      this.entries.delete(noteId);
    } else if (this.persist) {
      await new Promise<void>((resolve) => {
        const req = indexedDB.deleteDatabase(this.dbName(noteId));
        req.onsuccess = req.onerror = req.onblocked = () => resolve();
      });
    }
  }

  destroyAll() {
    for (const [id] of this.entries) this.close(id);
  }
}
